import type {
    CalendarRef,
    DateSelectInfo,
    DatesSetInfo,
    EventClickInfo,
    EventDisplayInfo,
    EventDropInfo,
    EventInput,
    EventResizeDoneInfo,
} from "@fullcalendar/react";
import { Calendar } from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/react/daygrid";
import interactionPlugin from "@fullcalendar/react/interaction";
import "@fullcalendar/react/skeleton.css";
import classicThemePlugin from "@fullcalendar/react/themes/classic";
import "@fullcalendar/react/themes/classic/palette.css";
import "@fullcalendar/react/themes/classic/theme.css";
import timeGridPlugin from "@fullcalendar/react/timegrid";
import { useCallback, useEffect, useRef, useState } from "react";
import { showConfirmDialog } from "../../../Logic/ConfirmDialog/showConfirmDialog";
import { CreateEvent, DeleteEvent, EventRequest, GetEvents, UpdateEvent } from "../../../Logic/EmsAPI/Events";
import { iEvent } from "../../../interfaces/iEvent";
import "./Calender.scss";
import EventFormModal, { EventFormValues } from "./EventFormModal";
import MonthYearTitlePicker from "./MonthYearTitlePicker";

const TITLE_PICKER_NAME = "titlePicker";
const DEFAULT_CATEGORY_ID = 2;

type EditorState = { mode: "create"; start: Date; end: Date } | { mode: "edit"; event: iEvent } | undefined;

export interface CalendarProps {
    userId: number;
    canCaptureEvents?: boolean;
}

function CalendarComponent(props: CalendarProps) {
    const canCaptureEvents = props.canCaptureEvents ?? true;
    const [events, setEvents] = useState<iEvent[]>([]);
    const [editorState, setEditorState] = useState<EditorState>(undefined);
    const visibleRange = useRef<{ start: Date; end: Date } | undefined>(undefined);
    const calendarRef = useRef<CalendarRef>(null);
    const [viewTitle, setViewTitle] = useState("");
    const [viewDate, setViewDate] = useState(new Date());

    const loadEvents = useCallback(
        async (start: Date, end: Date) => {
            const response = await GetEvents([props.userId], start, end);
            if (response.isOk && response.data) {
                setEvents(response.data.events);
            }
        },
        [props.userId],
    );

    async function refreshVisibleRange() {
        if (visibleRange.current) {
            await loadEvents(visibleRange.current.start, visibleRange.current.end);
        }
    }

    useEffect(() => {
        refreshVisibleRange();
    }, [props.userId]);

    function handleDatesSet(info: DatesSetInfo) {
        visibleRange.current = { start: info.start, end: info.end };
        setViewTitle(info.view.title);
        setViewDate(info.view.currentStart);
        loadEvents(info.start, info.end);
    }

    function handleSelect(info: DateSelectInfo) {
        if (info.view.type === "dayGridMonth") {
            calendarRef.current?.getApi().changeView("timeGridDay", info.start);
            return;
        }
        if (!canCaptureEvents) {
            return;
        }
        setEditorState({ mode: "create", start: info.start, end: info.end });
    }

    function handleEventClick(info: EventClickInfo) {
        if (!canCaptureEvents) {
            return;
        }
        const matchedEvent = events.find(x => x.id.toString() === info.event.id);
        if (matchedEvent) {
            setEditorState({ mode: "edit", event: matchedEvent });
        }
    }

    function handleJumpToMonth(year: number, month: number) {
        calendarRef.current?.getApi().gotoDate(new Date(year, month, 1));
    }

    function handleEventDidMount(info: EventDisplayInfo & { el: HTMLElement }) {
        const details = info.event.extendedProps.details;
        if (details) {
            info.el.setAttribute("title", details);
        }
    }

    function toRequest(values: {
        title: string;
        start: Date;
        end: Date;
        details?: string;
        categoryId?: number;
    }): EventRequest {
        return {
            Title: values.title,
            StartTime: values.start.toISOString(),
            EndTime: values.end.toISOString(),
            Details: values.details,
            UserId: props.userId,
            CategoryId: values.categoryId ?? DEFAULT_CATEGORY_ID,
        };
    }

    async function saveEventTimes(id: string, start: Date, end: Date): Promise<boolean> {
        const existing = events.find(x => x.id.toString() === id);
        if (!existing) {
            return false;
        }
        const response = await UpdateEvent(
            existing.id,
            toRequest({
                title: existing.title,
                start,
                end,
                details: existing.details,
                categoryId: existing.category?.id,
            }),
        );
        if (response.isOk) {
            await refreshVisibleRange();
        }
        return response.isOk;
    }

    async function handleEventDrop(info: EventDropInfo) {
        const success = await saveEventTimes(info.event.id, info.event.start!, info.event.end ?? info.event.start!);
        if (!success) {
            info.revert();
        }
    }

    async function handleEventResize(info: EventResizeDoneInfo) {
        const success = await saveEventTimes(info.event.id, info.event.start!, info.event.end!);
        if (!success) {
            info.revert();
        }
    }

    async function handleFormSave(values: EventFormValues) {
        if (editorState?.mode === "edit") {
            await UpdateEvent(editorState.event.id, toRequest(values));
        } else {
            await CreateEvent(toRequest(values));
        }
        setEditorState(undefined);
        await refreshVisibleRange();
    }

    async function handleFormDelete() {
        if (editorState?.mode !== "edit") {
            return;
        }
        const confirmed = await showConfirmDialog("Are you sure you want to delete this event?", {
            messageType: "warn",
        });
        if (!confirmed) {
            return;
        }
        await DeleteEvent(editorState.event.id);
        setEditorState(undefined);
        await refreshVisibleRange();
    }

    const calendarEvents: EventInput[] = events.map(event => {
        const categoryColor = event.category?.forgroundColour;
        return {
            id: event.id.toString(),
            title: event.title,
            start: event.startTime,
            end: event.endTime,
            extendedProps: { details: event.details },
            ...(categoryColor ? { color: categoryColor } : {}),
        };
    });

    return (
        <div className="calender-container">
            <Calendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, classicThemePlugin]}
                initialView="timeGridDay"
                headerToolbar={{
                    left: "prev,next today",
                    center: TITLE_PICKER_NAME,
                    right: "timeGridDay,timeGridWeek,dayGridMonth",
                }}
                toolbarElements={{
                    [TITLE_PICKER_NAME]: () => (
                        <MonthYearTitlePicker
                            title={viewTitle}
                            currentDate={viewDate}
                            onJump={handleJumpToMonth}
                        />
                    ),
                }}
                height="100%"
                editable={canCaptureEvents}
                selectable={true}
                events={calendarEvents}
                select={handleSelect}
                eventClick={handleEventClick}
                eventDrop={handleEventDrop}
                eventResize={handleEventResize}
                eventDidMount={handleEventDidMount}
                datesSet={handleDatesSet}
            />
            {editorState && (
                <EventFormModal
                    isEdit={editorState.mode === "edit"}
                    initialValues={
                        editorState.mode === "edit"
                            ? {
                                  title: editorState.event.title,
                                  start: new Date(editorState.event.startTime),
                                  end: new Date(editorState.event.endTime),
                                  details: editorState.event.details ?? "",
                                  categoryId: editorState.event.category?.id ?? DEFAULT_CATEGORY_ID,
                              }
                            : {
                                  title: "",
                                  start: editorState.start,
                                  end: editorState.end,
                                  details: "",
                                  categoryId: DEFAULT_CATEGORY_ID,
                              }
                    }
                    onSave={handleFormSave}
                    onDelete={editorState.mode === "edit" ? handleFormDelete : undefined}
                    onCancel={() => setEditorState(undefined)}
                />
            )}
        </div>
    );
}

export default CalendarComponent;
